import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import TopBar from './components/TopBar';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import UploadPage from './pages/UploadPage';
import ResultsPage from './pages/ResultsPage';
import ManufacturerVendorPage from './pages/ManufacturerVendorPage';
import BackendBUPage from './pages/BackendBUPage';
import GeminiTestPage from './pages/GeminiTestPage';
import NounModifierPage from './pages/NounModifierPage';
import PropertyConditionsPage from './pages/PropertyConditionsPage';
import TrainingDashboardPage from './pages/TrainingDashboardPage';
import AdminPage from './pages/AdminPage';
import SourceDataPage from './pages/SourceDataPage';

// ── Unified Footer ──────────────────────────────────────────────────
function CopyrightFooter() {
    return (
        <footer className="w-full border-t border-slate-200/60 bg-white/80 backdrop-blur-sm"
            style={{ zIndex: 10 }}
        >
            <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-slate-400 font-medium tracking-wide"
                   style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                    © {new Date().getFullYear()} <span className="text-slate-600 font-semibold">Banyan AI</span> · Built by{' '}
                    <span className="bg-gradient-to-r from-primary-500 to-cyan-500 bg-clip-text text-transparent font-semibold">
                        Joy Innovations Pvt Ltd
                    </span>
                </p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold
                                 bg-gradient-to-r from-primary-50 to-cyan-50 text-primary-700
                                 ring-1 ring-primary-200/50">
                    V6.9
                </span>
            </div>
        </footer>
    );
}

function AppLayout() {
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    const isLoginPage = location.pathname === '/login';
    const isFullWidth = location.pathname === '/results' || location.pathname === '/training' || location.pathname === '/source-data';

    // Don't show TopBar on login page
    if (isLoginPage) {
        return (
            <div className="min-h-screen app-shell" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1 }}>
                    <Routes>
                        <Route path="/login" element={
                            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
                        } />
                    </Routes>
                </div>
                <CopyrightFooter />
            </div>
        );
    }

    const mainClassName = isFullWidth
        ? 'w-full'
        : 'container mx-auto px-4 md:px-6 py-8 md:py-10';

    return (
        <div className="min-h-screen app-shell" style={{ display: 'flex', flexDirection: 'column' }}>
            <TopBar />

            <main className={mainClassName} style={{ flex: 1 }}>
                <Routes>
                    <Route path="/" element={
                        <ProtectedRoute menuKey="upload"><UploadPage /></ProtectedRoute>
                    } />
                    <Route path="/results" element={
                        <ProtectedRoute menuKey="results"><ResultsPage /></ProtectedRoute>
                    } />
                    <Route path="/source-data" element={
                        <ProtectedRoute menuKey="source_data"><SourceDataPage /></ProtectedRoute>
                    } />
                    <Route path="/manufacturer-vendor" element={
                        <ProtectedRoute menuKey="manufacturer_vendor"><ManufacturerVendorPage /></ProtectedRoute>
                    } />
                    <Route path="/backend-bu" element={
                        <ProtectedRoute menuKey="backend_bu"><BackendBUPage /></ProtectedRoute>
                    } />
                    <Route path="/test-gemini" element={
                        <ProtectedRoute><GeminiTestPage /></ProtectedRoute>
                    } />
                    <Route path="/noun-modifier" element={
                        <ProtectedRoute menuKey="noun_modifier"><NounModifierPage /></ProtectedRoute>
                    } />
                    <Route path="/property-conditions" element={
                        <ProtectedRoute menuKey="property_conditions"><PropertyConditionsPage /></ProtectedRoute>
                    } />
                    <Route path="/training" element={
                        <ProtectedRoute menuKey="training"><TrainingDashboardPage /></ProtectedRoute>
                    } />
                    <Route path="/admin" element={
                        <ProtectedRoute menuKey="admin"><AdminPage /></ProtectedRoute>
                    } />
                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </main>

            <CopyrightFooter />
        </div>
    );
}

function App() {
    return (
        <Router>
            <AuthProvider>
                <AppLayout />
            </AuthProvider>
        </Router>
    );
}

export default App;
